<!-- //
组件可视化开发-工作台设计
@auth xuyd
@date 2024-05-02
//-->
<template>
  <div style="width: 100%;height: 100%;">
    <div class="design-layout">
      <!-- 组件库容器 -->
      <div class="define-container" v-show="showSideBar">
        <el-tabs value="tabWidgetTemplate" type="border-card" style="height: 100%;">
          <el-tab-pane label="组件" name="tabWidgetTemplate" style="height: 100%;">
            <el-scrollbar class="scroll-container">
              <el-collapse class="template-container" v-model="selectedCollapse" :accordion="false">
                <el-collapse-item v-for="(item, index) in widgetTemplateMenu" :key="index" :name="index">
                  <div slot="title" class="title">{{ item.widgetTitle }}</div>
                  <div class="template-list">
                    <div class="template-item"
                         v-for="subItem in item.children"
                         :class="{dragItemLayout: true, templateWidgetList: true}"
                         :key="subItem.id"
                         draggable="true"
                         @dragstart="handleTemplateDragStart(subItem, $event)"
                         @dragend="handleTemplateDragEnd"
                    >
                      <svg-icon :icon="subItem.icon"/>
                      <div class="item-title">{{ subItem.widgetTitle }}</div>
                      <div class="item-tip" v-if="subItem.maxUse > 0">{{ subItem.maxUse }}</div>
                    </div>
                  </div>
                </el-collapse-item>
              </el-collapse>
            </el-scrollbar>
          </el-tab-pane>
          <el-tab-pane label="图层" name="tabWidgetLayer" style="height: 100%;">
            <el-scrollbar class="scroll-container">
              <draggable
                  v-model="widgetViewList"
                  :options="{ animation: 300 }"
              >
                <div class="template-item"
                     v-for="(layerItem, index) in widgetViewList"
                     :key="layerItem.id"
                     @mousedown="handleLayerWidgetClick(index)">
                  <svg-icon :icon="layerItem.icon"/>
                  <div class="item-title">{{ layerItem.widgetTitle }}</div>
                </div>
              </draggable>
            </el-scrollbar>
          </el-tab-pane>
        </el-tabs>
      </div>
      <!-- 分隔条 -->
      <WbSplitBar v-model="showSideBar" position="left"/>
      <!-- 工作台容器 -->
      <div class="workbench-container">
        <WbToolbar @click="handleToolCmd" :toolbar-data="toolbarData"/>

        <WbRulerTool class="workbench-ruler"
                     :step-length="50"
                     :parent="true"
                     :position="'relative'"
                     :is-scale-revise="true">
          <div class="grid-line">
            <div id="workbench" class="workbench"
                 :style="{
                transform: `scale(${toolbarData.scale})`,
                width: workbenchProps.width + 'px',
                height: workbenchProps.height + 'px',
                'background-color': workbenchProps.backgroundColor,
              }"
                 @click.self="handleWorkbenchClick"
                 @dragover="handleWidgetDragOver($event)"
                 @drop="handleWidgetDrop($event)"
            >

              <WidgetView class="widget-item"
                          ref="refDraggableWidget"
                          v-for="(widgetItem, index) in widgetViewList"
                          :widget="widgetItem"
                          :active="selectedWidget ? (selectedWidget.id === widgetItem.id) : false"
                          :scale="toolbarData.scale"
                          :key="index"
                          @onActivated="handleWidgetActivated(index, widgetItem)"
                          @contextmenu.prevent.native="handleWidgetCtxMenu(widgetItem, $event)"
                          @mousedown.prevent.native="handleWidgetMouseDown(index, widgetItem, $event)">
                <component :is="widgetItem.widgetProps.widgetViewName"
                           :widget-props="widgetItem.widgetProps"
                           :style="{
                            width: $util.num2px(widgetItem.widgetProps.viewProps.width),
                            height: $util.num2px(widgetItem.widgetProps.viewProps.height)
                          }"/>
              </WidgetView>
            </div>
          </div>
        </WbRulerTool>
      </div>
    </div>
    <!-- 右侧属性容器 -->
    <div class="props-container">
      <!-- 分隔条 -->
      <WbSplitBar v-model="showPropsBar" position="right"/>
      <!-- 右侧属性区域 -->
      <div class="props-form" v-show="showPropsBar">
        <component v-if="selectedWidget"
                   :is="selectedWidget.widgetProps.widgetFormName"
                   :widget-props="selectedWidget.widgetProps">
        </component>
        <component v-else :is="'WidgetWorkbench'" :widget-props="workbenchProps"/>
        <!-- 组件数据 -->
        <span style="color: #f1f1f1;width: 100%;word-break: break-all;">
          {{JSON.stringify(this.selectedWidget)}}
        </span>
      </div>
    </div>
    <WbContextMenu ref="refContextMenu" :widget-view-list="widgetViewList"/>
  </div>
</template>

<script>
import Draggable from "vuedraggable";
// import {widgetPackage} from '../../widget/index.js'; // 组件库
// import {widgetMenuList} from '../../widget/widgetMenu.js'; // 组件菜单
import * as WidgetUtil from '../../utils/util.js';
// import DataScreenApi from '@/api/datavisual/dataScreen.api.js';
import WbToolbar from "./wb-toolbar.vue";
import WbContextMenu from "./wb-context-menu.vue";
import WbSplitBar from "./wb-split-bar.vue";
import WbRulerTool from "vue-ruler-tool";
import WidgetWorkbench from "../widget-props/wb-workbench-props.vue";
import WidgetView from "../widget-view/index.vue";

// 工具栏配置的组件注册{FormXXX:{}, ViewXXX: {}}
// const regComponents = WidgetUtil.registerWidget(widgetMenuList, widgetPackage);

export default {
  name: 'WorkbenchDesignView',
  components: {
    Draggable
    , WbToolbar
    , WbRulerTool
    , WbSplitBar // 分隔条
    , WidgetWorkbench // 工作台属性组件
    , WidgetView
    , WbContextMenu
  },
  data() {
    return {
      widgetTemplateMenu: this.$util.listToTree(this.widgetMenuList),
      widgetTemplateMap: this.$util.listToMap(this.widgetMenuList, {idField: 'widgetName'}),
      draggingWidgetName: null, // 拖动的组件模板名
      draggingWidgetOffsetX: null, // 拖动的组件偏移x
      draggingWidgetOffsetY: null, // 拖动的组件偏移y
      selectedCollapse: 0, // 默认打开的组件面板

      // widgetViewList: [], // 工作区的所有View组件
      selectedWidget: null,// 选择的组件对象
      toolbarData: {scale: 0.6},// 工具栏参数
      // workbenchProps: {// 工作台组件数据
      //   id: null,
      //   width: 1920, // 工作台设计宽度
      //   height: 1080, // 工作台设计高度
      //   backgroundColor: '#1D202D', // 工作台背景色
      // },
    }
  },
  props:{
    pageId:String, // 当前设计工作台的页面数据Id
    workbenchProps: {
      type: Object,
      default: () => {
        return {
          width: 1920, // 工作台设计宽度
          height: 1080, // 工作台设计高度
          backgroundColor: '#1D202D', // 工作台背景色
        }
      }
    },
    widgetViewList: {
      type: Array,
      default: () => {
        return []
      }
    },
    widgetMenuList: {
      type: Array,
      default: () => {
        return []
      }
    },

  },
  computed: {
    showSideBar: {
      get() {
        return this.$store.getters.showSideBar;
      },
      set(v) {
        this.$store.dispatch('doShowSideBar', v)
      }
    },
    showPropsBar: {
      get() {
        return this.$store.getters.showPropsBar;
      },
      set(v) {
        this.$store.dispatch('doShowPropsBar', v)
      }
    }
  },

  async created() {
    // this.pageId = this.$route.query.pageId;
    // await this.getWorkbenchProps()
    // this.getList()
  },

  methods: {
    // /**
    //  * 查询工作台信息
    //  */
    // async getWorkbenchProps() {
    //   await DataScreenApi.getScreenInfo(this.pageId).then(data => {
    //     this.workbenchProps = data;
    //     // console.log('--------getWorkbenchProps--------->', this.workbenchProps)
    //   })
    // },

    // /**
    //  * 查询页面组件数据
    //  */
    // getList() {
    //   DataScreenApi.selectWidgetList(this.pageId).then(data => {
    //     let dataList = WidgetUtil.api2Vnode(this.widgetTemplateMap, data)
    //     this.widgetViewList.push(...dataList);
    //     // console.log('--------加载db数据--------->', data, this.widgetViewList)
    //   })
    // },

    // 左侧开始拖动组件
    handleTemplateDragStart(item, event) {
      this.draggingWidgetName = item.widgetName;
      this.draggingWidgetOffsetX = event.offsetX;
      this.draggingWidgetOffsetY = event.offsetY;
      // console.log("---handleTemplateDragStart---->", this.draggingWidgetName)
    },

    // 左侧拖动组件结束
    handleTemplateDragEnd(val) {
      // console.log("---handleTemplateDragEnd---->", val, this.widgetViewList)
    },

    // 进入工作区
    handleWidgetDragOver(event) {
      // event.preventDefault();
      // event.stopPropagation();
      // event.dataTransfer.dropEffect = "copy";
    },

    /**
     * 拖动结束，设置根据x,y坐标放置
     */
    handleWidgetDrop(event) {
      let eventX = event.clientX;
      let eventY = event.clientY;
      let wbPosition = this.getElementTopLeftById('workbench')
      // 计算相对于工作区位置，且去掉在widget内部的偏移量
      // 得到组件左上角相对于工作区坐标
      let widgetInWorkbenchTop = eventY - wbPosition.top - this.draggingWidgetOffsetY;
      let widgetInWorkbenchLeft = eventX - wbPosition.left - this.draggingWidgetOffsetX;
      let position = {top: widgetInWorkbenchTop, left: widgetInWorkbenchLeft};

      // 复制并设置Id
      let widgetClone = WidgetUtil.copyVnodeByName(this.widgetTemplateMap[this.draggingWidgetName]);
      widgetClone.id = null;
      widgetClone.pageId = this.pageId;
      widgetClone.widgetProps.viewProps.top = Number(position.top / this.toolbarData.scale).toFixed(0);
      widgetClone.widgetProps.viewProps.left = Number(position.left / this.toolbarData.scale).toFixed(0);
      this.$emit('add', widgetClone).then((dbData)=>{
        let widgetData = WidgetUtil.api2Vnode(this.widgetTemplateMap, [dbData])[0];
        this.widgetViewList.push(widgetData);
        this.selectedWidget = dbData;
        console.log("----当前工作台组件：----->", this.widgetViewList);
      })
    },

    /**
     * 图层点击
     * @param index
     */
    handleLayerWidgetClick(index) {
      console.log("----handleLayerWidgetClick：----->", index);
      let w = this.widgetViewList[index];
      this.selectedWidget = w;
    },
    /**
     * 获取element在屏幕的位置
     * @param id
     * @returns {{top: number, left: number}}
     */
    getElementTopLeftById(id) {
      let el = document.getElementById(id)
      let top = 0
      let left = 0
      if (el != null) {
        top = el.getBoundingClientRect().top
        left = el.getBoundingClientRect().left
      }
      return {top: top, left: left}
    },

    /**
     * 打开上下文菜单
     */
    handleWidgetCtxMenu(widgetItem, e) {
      console.log("----handleWidgetCtxMenu--->", widgetItem, e.target)
      const menuMinWidth = 150
      const offsetLeft = this.$el.getBoundingClientRect().left // container margin left
      const offsetWidth = this.$el.offsetWidth // container width
      const maxLeft = offsetWidth - menuMinWidth + offsetLeft // left boundary
      const eventX = e.clientX
      let left;
      if (eventX > maxLeft) {
        left = maxLeft
      } else {
        left = eventX
      }
      let top = e.clientY
      this.selectedWidget = widgetItem
      this.$refs.refContextMenu.openMenu({left: left, top: top, widgetData: widgetItem});
    },

    /**
     * 点击工作台
     */
    handleWorkbenchClick() {
      console.log('点击了工作台')
      this.selectedWidget = null;
    },

    /**
     * 点击组件
     */
    handleWidgetMouseDown(index, widgetItem, event) {
      // event.preventDefault();
      // event.stopPropagation();
      const widgets = this.$refs.refDraggableWidget;
      for (let i = 0; i < widgets.length; i++) {
        if (i === index) {
          // widgets[i].setActive(true);
        } else {
          // widgets[i].setActive(false);
        }
      }
      this.selectedWidget = widgetItem;
      this.$refs.refContextMenu.closeMenu();
    },

    /**
     * 监听组件
     */
    handleWidgetActivated(index, widgetItem, activated) {
      console.log("----handleWidgetActivated--->", widgetItem, activated)
    },


    /**
     * 工具栏操作，保存，预览等
     *
     * @param cmd
     * @param value
     */
    handleToolCmd({cmd, value}) {
      switch (cmd) {
        case 'save':
          console.log("==========保存数据开始===========>")
            this.$emit('save');
          break;
        case 'preview':
          this.$emit('preview');
          break;
        case 'import':
          this.$modal.msg('开发中');
          break;
        case 'export':
          this.$modal.msg('开发中');
          break;
        default:
          break;
      }
    },
  }
}
</script>

<style lang="less" scoped>
@import '../../assets/css/variables.less';

.screen-design {
  .el-form-item__label {
    color: @dv_form_label_color !important;
    font-size: 12px !important;
  }

  .el-form-item {
    margin-bottom: 5px !important;
  }

  .el-input__inner {
    background-color: @dv_form_input_background !important;
    border: 1px solid @dv_form_input_border !important;
    color: @dv_form_input_color;
    padding: 0 8px;
  }

  .el-input-group__append {
    border-left: 0;
  }

  .el-input-group__prepend {
    border-right: 0;
  }

  .el-input-number__decrease,
  .el-input-number__increase,
  .el-input-group__append,
  .el-input-group__prepend {
    color: @dv_form_input_color !important;
    background-color: @dv_form_input_border !important;
    border: 1px solid @dv_form_input_border !important;
  }

  .el-tabs--border-card > .el-tabs__content {
    padding: 10px 10px;
    height: calc(100% - 40px) !important;
  }

  .el-tabs--border-card {
    background-color: @dv_background_fe !important;
    border: 0;
    box-shadow: 0 0 0 0;

    .el-tabs__header {
      background-color: @dv_background_bg !important;
      border-width: 0;

      .el-tabs__item {
        border-width: 0;
      }

      .el-tabs__item.is-active {
        background-color: @dv_background_fe !important;
      }
    }
  }
}
</style>

<style lang="less" scoped>
@import '../../assets/css/variables.less';
@import './workbench_.less';
</style>
